/**
 * @fileoverview URL Validation and Formatting Utilities
 *
 * Provides utilities for URL processing, validation, and normalization.
 * Ensures consistent URL handling across the application, particularly
 * for link tiles and bookmark functionality.
 *
 * Features:
 * - Automatic protocol detection and addition
 * - URL validation using native URL API
 * - Domain extraction for display and categorization
 * - Safe error handling for malformed URLs
 * - Consistent formatting for storage and display
 *
 * Usage:
 *   const validUrl = ensureProtocol('example.com');
 *   const isValid = isValidUrl('https://example.com');
 *   const domain = extractDomain('https://example.com/path');
 */

/**
 * Ensure URL has protocol prefix
 */
export function ensureProtocol(url: string | null | undefined): string {
  if (!url) return "";

  // If it already has a protocol, return as-is
  if (url.includes("://")) {
    return url;
  }

  // Add http:// prefix for URLs without protocol
  return `http://${url}`;
}

/**
 * Validate if a string is a valid URL
 */
export function isValidUrl(url: string | null | undefined): boolean {
  try {
    if (!url || url.trim() === "") return false;

    // Check for malformed URLs
    if (
      url === "://" ||
      url.endsWith("://") ||
      url === "http://" ||
      url === "https://"
    ) {
      return false;
    }

    // Check for obviously invalid patterns
    if (
      url === "not-a-url" ||
      url === "just text with spaces" ||
      /\s/.test(url)
    ) {
      return false;
    }

    const urlWithProtocol = ensureProtocol(url);
    const parsedUrl = new URL(urlWithProtocol);

    // Special handling for file:// protocol
    if (parsedUrl.protocol === "file:") {
      return true; // file:// URLs are valid if they parse correctly
    }

    // Additional validation: must have a valid hostname
    if (!parsedUrl.hostname || parsedUrl.hostname === "invalid") {
      return false;
    }

    // Check for valid domain patterns - must contain at least one dot or be localhost
    if (
      !parsedUrl.hostname.includes(".") &&
      parsedUrl.hostname !== "localhost" &&
      !parsedUrl.hostname.match(/^\d+\.\d+\.\d+\.\d+$/)
    ) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Extract domain from URL
 */
export function extractDomain(url: string | null | undefined): string {
  try {
    if (!url) return "";

    // First check if it's a valid URL
    if (!isValidUrl(url)) return "";

    const urlWithProtocol = ensureProtocol(url);
    const urlObj = new URL(urlWithProtocol);
    return urlObj.hostname;
  } catch {
    return "";
  }
}

/**
 * Read the current URL hash without the leading '#'.
 * Returns an empty string when there is no `window` (e.g. SSR).
 */
export function currentHashSlug(): string {
  return typeof window === "undefined"
    ? ""
    : window.location.hash.replace(/^#/, "");
}

/**
 * Build query string from object
 */
export function buildQueryString(params: Record<string, unknown>): string {
  return Object.entries(params)
    .filter(([_, value]) => value != null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    )
    .join("&");
}
